import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  CARE_PASS_ACTION_LABELS,
  type CarePassAction,
  SCHEDULED_CARE_PASS_ACTIONS,
} from "@alm/domain";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { useFacility } from "@/lib/facility/facility";
import { ChevronLeft } from "@/lib/icons";
import { guardPermission } from "@/lib/policy/route-guard";
import { WeekEditor } from "./-components/week-editor";

export const Route = createFileRoute("/_authenticated/_care/care-pass/schedule")({
  beforeLoad: ({ context }) => guardPermission(context.me, "carepass:manage", "/care-pass"),
  component: CareSchedulePage,
});

const ACTION_OPTIONS = SCHEDULED_CARE_PASS_ACTIONS.map((action) => ({
  value: action,
  label: CARE_PASS_ACTION_LABELS[action],
}));

/** Each resident's care week, one kind of care at a time: which day and shift their laundry, room
 * cleaning and bathing fall on. A cell sets one day; a selection sets the same week for many. */
function CareSchedulePage() {
  const { facilityId } = useFacility();
  const realFacility = facilityId === "all" ? "" : facilityId;
  const [action, setAction] = useState<CarePassAction>("laundry");
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
      <PageHeader
        title="Care schedule"
        meta="Who gets what on which day and shift. Supervision needs no schedule: every resident is owed it on every shift."
        actions={
          <>
            <ToggleGroup
              type="single"
              variant="outline"
              aria-label="Care pass action"
              value={action}
              onValueChange={(next) => {
                const picked = ACTION_OPTIONS.find((option) => option.value === next);
                if (picked) setAction(picked.value);
              }}
            >
              {ACTION_OPTIONS.map((option) => (
                <ToggleGroupItem key={option.value} value={option.value}>
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <Button variant="outline" asChild>
              <Link to="/care-pass">
                <ChevronLeft className="size-4" />
                Care pass
              </Link>
            </Button>
          </>
        }
      />
      {realFacility ? (
        <WeekEditor key={`${realFacility}-${action}`} facilityId={realFacility} action={action} />
      ) : (
        <OneBuildingCard prompt="Choose a building to build its care schedule." />
      )}
    </div>
  );
}
